/**
 * @file Region Highlight Manager
 * @description Map and item highlighting logic for region component
 */

import { SELECTORS, HIGHLIGHT } from "./constants.js";
import {
  getElement,
  getElementsBySelector,
  getElementById,
  getElementBySelector,
  addClass,
  removeClass,
  setStyle,
} from "./dom-manager.js";
import { storeOriginalStyles, getOriginalRegionStyles } from "./state.js";

/**
 * Highlight region on map
 * @param {string} regionName - Region name
 */
export async function highlightRegionOnMap(regionName) {
  if (!regionName) return;

  // Store original styles if not already stored
  const { getStateStats } = await import("./state.js");
  const stats = getStateStats();
  if (stats.originalStyles === 0) {
    storeOriginalStyles(SELECTORS.MAPSVG_REGIONS);
  }

  // Remove highlight from all regions first
  const allRegions = getElementsBySelector(SELECTORS.MAPSVG_REGIONS);
  allRegions.forEach((region) => {
    const original = getOriginalRegionStyles(region.id);
    if (original) {
      setStyle(region, "fill", original.style.fill);
      setStyle(region, "stroke", original.style.stroke);
      setStyle(region, "opacity", original.style.opacity);
      setStyle(region, "filter", original.style.filter);
    }
  });

  // Highlight the specific region (only within vietnam-map)
  const regionElement = document.querySelector(`${SELECTORS.VIETNAM_MAP_REGIONS}#${regionName}`);
  if (regionElement) {
    setStyle(regionElement, "filter", HIGHLIGHT.MAP_FILTER);
    setStyle(regionElement, "opacity", "1");
  }
}

/**
 * Remove highlight from all regions
 */
export function removeRegionHighlight() {
  // Only remove highlight from vietnam-map regions, not child-map regions
  const allRegions = getElementsBySelector(SELECTORS.VIETNAM_MAP_REGIONS);
  allRegions.forEach((region) => {
    const original = getOriginalRegionStyles(region.id);
    if (original) {
      setStyle(region, "fill", original.style.fill);
      setStyle(region, "stroke", original.style.stroke);
      setStyle(region, "opacity", original.style.opacity);
      setStyle(region, "filter", original.style.filter);
    } else {
      setStyle(region, "filter", "");
      setStyle(region, "opacity", "");
    }
  });
}

/**
 * Highlight region item in list when hovering on map
 * @param {string} regionName - Region name/slug
 */
export function highlightRegionItem(regionName) {
  if (!regionName) return;

  // Only add hover highlight class, don't use inline styles
  // Remove hover highlight from all items first
  const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  allItems.forEach((item) => {
    removeClass(item, "hover-highlight");
  });

  // Highlight the specific item (only if not already active)
  const itemElement = getElementBySelector(
    `${SELECTORS.REGION_ITEMS}[data-region="${regionName}"]`,
  );
  if (itemElement && !itemElement.classList.contains("active")) {
    addClass(itemElement, "hover-highlight");
  }
}

/**
 * Remove highlight from all region items
 */
export function removeRegionItemHighlight() {
  // Only remove hover highlight, keep active state
  const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  allItems.forEach((item) => {
    removeClass(item, "hover-highlight");
  });
}

/**
 * Set active state for region item
 * @param {string} regionName - Region name/slug
 */
export function setActiveRegionItem(regionName) {
  if (!regionName) return;

  // Remove active class from all items
  const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  allItems.forEach((item) => {
    removeClass(item, "active");
  });

  // Add active class to specific item
  const itemElement = getElementBySelector(
    `${SELECTORS.REGION_ITEMS}[data-region="${regionName}"]`,
  );
  if (itemElement) {
    addClass(itemElement, "active");
  }
}

/**
 * Remove active state from all region items
 */
export function removeActiveRegionItems() {
  const allItems = getElementsBySelector(SELECTORS.REGION_ITEMS);
  allItems.forEach((item) => {
    removeClass(item, "active");
  });
}

/**
 * Highlight multiple regions
 * @param {Array<string>} regionNames - Array of region names
 */
export async function highlightMultipleRegions(regionNames) {
  if (!Array.isArray(regionNames) || regionNames.length === 0) return;

  // Store original styles if not already stored
  const { getStateStats } = await import("./state.js");
  const stats = getStateStats();
  if (stats.originalStyles === 0) {
    storeOriginalStyles(SELECTORS.MAPSVG_REGIONS);
  }

  // Remove highlight from all regions first
  removeRegionHighlight();

  // Highlight each region
  regionNames.forEach((regionName) => {
    const regionElement = getElementById(regionName);
    if (regionElement) {
      setStyle(regionElement, "filter", HIGHLIGHT.MAP_FILTER);
      setStyle(regionElement, "opacity", "1");
    }
  });
}

/**
 * Highlight region with custom styles
 * @param {string} regionName - Region name
 * @param {Object} customStyles - Custom highlight styles
 */
export function highlightRegionWithCustomStyles(regionName, customStyles) {
  if (!regionName) return;

  const regionElement = getElementById(regionName);
  if (regionElement) {
    Object.entries(customStyles).forEach(([property, value]) => {
      setStyle(regionElement, property, value);
    });
  }
}

/**
 * Get highlight status for a region
 * @param {string} regionName - Region name
 * @returns {Object} Highlight status
 */
export function getRegionHighlightStatus(regionName) {
  if (!regionName) return null;

  const regionElement = getElementById(regionName);
  if (!regionElement) return null;

  return {
    hasFilter: !!regionElement.style.filter,
    hasOpacity: !!regionElement.style.opacity,
    filter: regionElement.style.filter,
    opacity: regionElement.style.opacity,
  };
}

/**
 * Reset all highlighting
 */
export function resetAllHighlighting() {
  removeRegionHighlight();
  removeRegionItemHighlight();
  removeActiveRegionItems();
}
